import { Box } from "@mui/material";
import classNames from "classnames";
import { PluggableList } from "node_modules/react-markdown/lib";
import * as React from "react";
import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import rehypeExternalLinks from "rehype-external-links";
import remarkEmoji from "remark-emoji";
import remarkGfm from "remark-gfm";

import {
  isDatasetLink,
  isFileLink,
  isMessagePathLink,
  isTimestampLink,
  isTopicLink,
  isRobotoUri,
} from "@/shared/components/RobotoMarkdown/magicLinks.ts";
import { browserSupportsNamedGroups } from "@/shared/text";

import { preserveBlankLines } from "./helpers";
import { remarkUserMentions } from "./remarkUserMentions";
import styles from "./RobotoMarkdown.module.css";
import {
  RobotoMarkdownLink,
  DatasetLinkRenderer,
  FileLinkRenderer,
  MessagePathLinkRenderer,
  TopicLinkRenderer,
} from "./RobotoMarkdownLink";
import { useMarkdownClipboardCopy } from "./useMarkdownClipboardCopy";

type RobotoMarkdownProps = {
  content: string;
  /**
   * Additional CSS class name to apply to the markdown container.
   * Can be used to style individual HTML elements rendered from markdown,
   * similar to how RobotoMarkdown.module.css is used.
   */
  className?: string;
  variant?: "normal" | "compact";
  datasetLinkRenderer?: DatasetLinkRenderer;
  fileLinkRenderer?: FileLinkRenderer;
  topicLinkRenderer?: TopicLinkRenderer;
  messagePathLinkRenderer?: MessagePathLinkRenderer;
  onLinkClick?: () => void;
};

export const RobotoMarkdown: React.FC<RobotoMarkdownProps> = ({
  content,
  className,
  datasetLinkRenderer,
  fileLinkRenderer,
  topicLinkRenderer,
  messagePathLinkRenderer,
  onLinkClick,
  variant = "compact",
}: RobotoMarkdownProps) => {
  // Conditionally create remarkPlugins based on named group support
  const remarkPlugins: PluggableList = [
    remarkEmoji,
    ...(browserSupportsNamedGroups() ? [remarkGfm] : []),
    remarkUserMentions,
  ];

  const rehypePlugins: PluggableList = [
    [rehypeExternalLinks, { target: "_blank" }],
  ];

  // Preserve a visible gap when authors typed three+ newlines. CommonMark
  // collapses any run of empty lines into a single paragraph break, so the
  // extra blank line gets lost without a pre-parse nudge.
  const formattedContent = preserveBlankLines(content);

  const containerClassName = React.useMemo(() => {
    const classNamesToCombine = [];
    if (variant === "compact") {
      classNamesToCombine.push(styles.markdownVariantCompact);
    } else if (variant === "normal") {
      classNamesToCombine.push(styles.markdownVariantNormal);
    }

    if (className) {
      classNamesToCombine.push(className);
    }

    if (classNamesToCombine.length === 0) {
      return undefined;
    }

    return classNames(...classNamesToCombine);
  }, [className, variant]);

  const onCopy = useMarkdownClipboardCopy();

  return (
    <Box data-cy="markdown-container" onCopy={onCopy}>
      <ReactMarkdown
        rehypePlugins={rehypePlugins}
        remarkPlugins={remarkPlugins}
        className={containerClassName}
        urlTransform={(url) => {
          if (
            isTimestampLink(url) ||
            isDatasetLink(url) ||
            isFileLink(url) ||
            isTopicLink(url) ||
            isMessagePathLink(url) ||
            isRobotoUri(url)
          ) {
            return url;
          }

          return defaultUrlTransform(url);
        }}
        components={{
          a: ({ href, children }) => {
            return (
              <RobotoMarkdownLink
                href={href}
                datasetLinkRenderer={datasetLinkRenderer}
                fileLinkRenderer={fileLinkRenderer}
                topicLinkRenderer={topicLinkRenderer}
                messagePathLinkRenderer={messagePathLinkRenderer}
                onLinkClick={onLinkClick}
              >
                {children}
              </RobotoMarkdownLink>
            );
          },
        }}
      >
        {formattedContent}
      </ReactMarkdown>
    </Box>
  );
};
